// Two-column mono move list: click-to-jump, grade dots with text tooltips,
// auto-scroll, "Return to latest" pill while viewing history.

import { useEffect, useRef } from 'react';
import type { Grade, MoveRow } from '../../shared/types.ts';
import { isPass, isResign } from '../../shared/go/coords.ts';
import { useStore } from '../store/useStore.ts';

const GRADE_LABEL: Record<Grade, string> = {
  excellent: 'Excellent',
  good: 'Good',
  inaccuracy: 'Inaccuracy',
  mistake: 'Mistake',
  blunder: 'Blunder',
};

/** Mover-perspective winrate delta as a signed percent string, e.g. '−0.4%'. */
function moverDelta(m: MoveRow): string | null {
  if (m.winrateBefore === null || m.winrateAfter === null) return null;
  const blackDelta = m.winrateAfter - m.winrateBefore;
  const delta = (m.color === 'B' ? blackDelta : -blackDelta) * 100;
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '±';
  return `${sign}${Math.abs(delta).toFixed(1)}%`;
}

function moveLabel(m: MoveRow): string {
  if (isPass(m.vertex)) return 'pass';
  if (isResign(m.vertex)) return 'resign';
  return m.vertex;
}

function MoveCell({ move, selected }: { move: MoveRow; selected: boolean }) {
  const setCursor = useStore((s) => s.setCursor);
  const delta = moverDelta(move);
  const gradeTitle = move.grade
    ? `${GRADE_LABEL[move.grade]}${delta ? `, ${delta}` : ''}`
    : undefined;
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      className={`move-cell mono ${selected ? 'is-selected' : ''}`}
      onClick={() => setCursor(move.moveNumber)}
      title={gradeTitle}
    >
      <span className="move-num">{move.moveNumber}</span>
      <span className="move-vertex">{moveLabel(move)}</span>
      {move.grade && (
        <span className={`grade-dot grade-${move.grade}`} aria-hidden="true" />
      )}
      {move.grade && <span className="sr-only">{gradeTitle}</span>}
    </button>
  );
}

export function MoveList() {
  const detail = useStore((s) => s.detail);
  const cursor = useStore((s) => s.cursor);
  const setCursor = useStore((s) => s.setCursor);
  const listRef = useRef<HTMLDivElement>(null);
  const moveCount = detail?.moves.length ?? 0;

  useEffect(() => {
    if (cursor === null && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [moveCount, cursor]);

  if (!detail) return null;
  const moves = detail.moves;
  const viewed = cursor ?? moves.length;

  const rows = [];
  for (let i = 0; i < moves.length; i += 2) {
    const black = moves[i]!;
    const white = moves[i + 1];
    rows.push(
      <div className="move-row" key={i}>
        <MoveCell move={black} selected={viewed === black.moveNumber} />
        {white ? (
          <MoveCell move={white} selected={viewed === white.moveNumber} />
        ) : (
          <span className="move-cell move-cell-empty" aria-hidden="true" />
        )}
      </div>,
    );
  }

  return (
    <div className="move-list-wrap">
      <div
        ref={listRef}
        className="move-list"
        role="listbox"
        aria-label="Move list; use left and right arrow keys to step through the game"
      >
        {moves.length === 0 ? (
          <p className="empty-note">No moves yet — Black to open.</p>
        ) : (
          rows
        )}
      </div>
      <div className="grade-legend" aria-hidden="true">
        <span>
          <span className="grade-dot grade-excellent" /> excellent
        </span>
        <span>
          <span className="grade-dot grade-good" /> good
        </span>
        <span>
          <span className="grade-dot grade-inaccuracy" /> inaccuracy
        </span>
        <span>
          <span className="grade-dot grade-mistake" /> mistake
        </span>
        <span>
          <span className="grade-dot grade-blunder" /> blunder
        </span>
      </div>
      {cursor !== null && (
        <button type="button" className="latest-pill" onClick={() => setCursor(null)}>
          Return to latest ↦
        </button>
      )}
    </div>
  );
}
